import {catchError, defer, EMPTY, exhaustMap, from, map, merge, mergeMap, Observable, of, OperatorFunction, switchMap, take, tap, throwIfEmpty} from 'rxjs';
import {Action} from '@ngrx/store';
import {MxDialogService} from '@aviary/store/common/mx-dialog.service';
import {inject} from '@angular/core';
import {asArray} from '@aviary/store/common/typing.helpers';
import {ConfirmationDialog, ConfirmationDialogIngredients} from '@aviary/dialogs/confirmation-dialog/confirmation-dialog.types';
import {NotificationDialog, NotificationDialogIngredients} from '@aviary/dialogs/notification-dialog/notification-dialog.types';
import {FormDialog, FormDialogIngredients} from '@aviary/dialogs/form-dialog/form-dialog.types';
import {PersistentFormDialog, PersistentFormDialogIngredients} from '@aviary/dialogs/persistent-form-dialog/persistent-form-dialog.types';
import {MatDialogRef} from '@angular/material/dialog';
import {PersistentFormDialogController} from '@aviary/dialogs/persistent-form-dialog/persistent-form-dialog.controller';

export function openNotificationDialog<Input, DataType, DialogComponent extends NotificationDialog<DataType>>(
  inputMapper: (input: Input) => NotificationDialogIngredients<DataType, DialogComponent>,
  dialogService: MxDialogService = inject(MxDialogService)
): OperatorFunction<Input, Action> {
  return switchMap(input => {
    const ingredients: NotificationDialogIngredients<DataType, DialogComponent> = inputMapper(input);
    return dialogService.openNotificationDialog<DataType, DialogComponent>(ingredients.componentClass, ingredients.data)
      .afterClosed()
      .pipe(
        mergeMap(() => [
          ...asArray(ingredients.onConfirm?.()),
          ...asArray(ingredients.onClose?.())
        ])
      );
  });
}

export function openConfirmationDialog<Input, DataType, ValueType, DialogComponent extends ConfirmationDialog<DataType, ValueType>>(
  inputMapper: (input: Input) => ConfirmationDialogIngredients<DataType, ValueType, DialogComponent>,
  dialogService: MxDialogService = inject(MxDialogService)
): OperatorFunction<Input, Action> {
  return switchMap(input => {
    const ingredients: ConfirmationDialogIngredients<DataType, ValueType, DialogComponent> = inputMapper(input);
    return dialogService.openConfirmationDialog<DataType, ValueType, DialogComponent>(ingredients.componentClass, ingredients.data)
      .afterClosed()
      .pipe(
        mergeMap(value => [
          ...asArray(value ? ingredients.onConfirm(value) : ingredients.onCancel?.()),
          ...asArray(ingredients.onClose?.())
        ])
      );
  });
}

export function openFormDialog<
  Input,
  DataSupertype, DataType extends DataSupertype,
  ValueSupertype, ValueType extends ValueSupertype,
  DialogComponent extends FormDialog<DataSupertype, ValueSupertype>
>(
  inputMapper: (input: Input) => FormDialogIngredients<DataSupertype, DataType, ValueSupertype, ValueType, DialogComponent>,
  dialogService: MxDialogService = inject(MxDialogService)
): OperatorFunction<Input, Action> {
  return switchMap(input => {
    const ingredients: FormDialogIngredients<DataSupertype, DataType, ValueSupertype, ValueType, DialogComponent> = inputMapper(input);

    return dialogService.openFormDialog<DataSupertype, DataType, ValueSupertype, ValueType, DialogComponent>(ingredients.componentClass, ingredients.data)
      .afterClosed()
      .pipe(
        mergeMap(value => [
          ...asArray(value ? ingredients.onConfirm(value) : ingredients.onCancel?.()),
          ...asArray(ingredients.onClose?.()),
        ])
      );
  });
}

export function openPersistentFormDialog<
  Input,
  DataSupertype, DataType extends DataSupertype,
  ValueSupertype, ValueType extends ValueSupertype,
  ResultType,
  DialogComponent extends PersistentFormDialog<DataSupertype, ValueSupertype>
>(
  inputMapper: (input: Input) => PersistentFormDialogIngredients<DataSupertype, DataType, ValueSupertype, ValueType, ResultType, DialogComponent>,
  dialogService: MxDialogService = inject(MxDialogService)
): OperatorFunction<Input, Action> {
  return switchMap(input => {
    const ingredients: PersistentFormDialogIngredients<DataSupertype, DataType, ValueSupertype, ValueType, ResultType, DialogComponent> = inputMapper(input);

    const {dialogRef, controller}: { dialogRef: MatDialogRef<DialogComponent, void>; controller: PersistentFormDialogController<ValueType> } =
      dialogService.openPersistentFormDialog<DataSupertype, DataType, ValueSupertype, ValueType, DialogComponent>(ingredients.componentClass, ingredients.data);

    const dialog: DialogComponent = dialogRef.componentInstance;
    let submissionSucceeded: boolean = false;
    let successActions: Action[] = [];

    const submissionActions: Observable<Action> = controller.submissions.pipe(
      exhaustMap(value => defer(() => ingredients.onConfirm(value, dialog)).pipe(
        take(1),
        throwIfEmpty(() => new Error('Persistent form dialog submission completed without a result')),
        map(result => ({successful: true as const, result})),
        catchError((error: unknown) => {
          controller.resetAfterFailure();
          dialog.handleSubmissionFailure?.(error);
          return of({successful: false as const, error});
        }),
        mergeMap(outcome => {
          if (!outcome.successful) return from(asArray<Action>(ingredients.onFailure?.(outcome.error, dialog)));

          successActions = asArray<Action>(ingredients.onSuccess(outcome.result, value));
          submissionSucceeded = true;
          dialogRef.close();
          return EMPTY;
        })
      ))
    );

    const closeActions: Observable<Action> = dialogRef.afterClosed().pipe(
      tap(() => controller.complete()),
      mergeMap(() => [
        ...submissionSucceeded ? successActions : asArray<Action>(ingredients.onCancel?.()),
        ...asArray<Action>(ingredients.onClose?.())
      ])
    );

    return merge(submissionActions, closeActions);
  });
}
